import {
    DeliveryStatus,
    StockStatus,
    getDeliveryStatusTranslationId,
    getStockStatusTranslationId,
} from '@synergy/models';
import { Field, FieldProps } from 'formik';
import { FormHelperText, Grid } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { fields } from '@synergy/utils';

import {
    PartialSupplier,
    PoolSupplierAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/supplier-autocomplete-list/pool-supplier-autocomplete-list';
import { ProductDetailOverviewFormValues } from '../models/product-detail-overview-form-values';

export const ProductDetailOverviewSectionDelivery = () => {
    const fieldNames = fields<ProductDetailOverviewFormValues>();

    const stockOptions = Object.values(StockStatus).map<SelectOption>(key => ({
        value: key,
        displayValue: <FormattedMessage id={getStockStatusTranslationId(key)} />,
    }));

    const deliveryOptions = Object.values(DeliveryStatus).map<SelectOption>(key => ({
        value: key,
        displayValue: <FormattedMessage id={getDeliveryStatusTranslationId(key)} />,
    }));

    return (
        <>
            <PoolTypography styleType="sectionTitle" id="app.product.detail.deliveryDetail" />
            <Grid container spacing={2}>
                <Grid item={true} xs={12}>
                    <Field name={fieldNames.supplier}>
                        {({ field, form, meta }: FieldProps<PartialSupplier>) => (
                            <>
                                <PoolSupplierAutocompleteList
                                    label={<FormattedMessage id="app.freePosition.supplierAccount" />}
                                    onSelected={supplier => form.setFieldValue(field.name, supplier)}
                                    initialValue={field.value?.name}
                                    error={meta.touched && !!meta.error}
                                    disablePreventOverflow={true}
                                    required={true}
                                    {...field}
                                />
                                {meta.touched && meta.error && (
                                    <FormHelperText error={true}>
                                        <FormattedMessage id={meta.error} />
                                    </FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                <Grid container={true} item={true} spacing={2}>
                    <Grid item={true} xs={4}>
                        <Field name={fieldNames.stockStatus}>
                            {({ form, field, meta }: FieldProps<number>) => (
                                <>
                                    <PoolSelect
                                        name="stockStatus"
                                        fullWidth={true}
                                        label={<FormattedMessage id="app.label.stockStatus" />}
                                        options={stockOptions}
                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                        error={meta.touched && !!meta.error}
                                        value={field.value}
                                        noSorting={true}
                                        disallowDefaultValue={true}
                                    />
                                    {meta.error && (
                                        <FormHelperText error={true}>
                                            <FormattedMessage id={meta.error} />
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4}>
                        <Field name={fieldNames.inventory}>
                            {({ form, field, meta }: FieldProps<number>) => (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.label.inventory" />}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    fullWidth={true}
                                    isNumeric={true}
                                    noNegativeNumbers={true}
                                    disabled={
                                        form.values.stockStatus !== StockStatus.ManageStock ||
                                        form.values.deliveryStatus === DeliveryStatus.Unavailable
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
                <Grid container={true} item={true} spacing={2}>
                    <Grid item={true} xs={4}>
                        <Field name={fieldNames.deliveryStatus}>
                            {({ form, field, meta }: FieldProps<number>) => (
                                <>
                                    <PoolSelect
                                        name="deliveryStatus"
                                        fullWidth={true}
                                        label={<FormattedMessage id="app.label.deliveryStatus" />}
                                        options={deliveryOptions}
                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                        error={meta.touched && !!meta.error}
                                        value={field.value}
                                        noSorting={true}
                                        disallowDefaultValue={true}
                                    />
                                    {meta.error && (
                                        <FormHelperText error={true}>
                                            <FormattedMessage id={meta.error} />
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4}>
                        <Field name={fieldNames.deliveryTimeInDays}>
                            {({ form, field, meta }: FieldProps<number>) => (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.product.detail.deliveryTime" />}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    fullWidth={true}
                                    isNumeric={true}
                                    noNegativeNumbers={true}
                                    required={form.values.deliveryStatus === DeliveryStatus.Tracked}
                                    disabled={
                                        form.values.deliveryStatus !== DeliveryStatus.Tracked ||
                                        form.values.stockStatus === StockStatus.SoldOut
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
